'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { toEnglishDigits } from '@hamishon/shared-utils';

type DynamicFormFieldType =
  | 'TEXT'
  | 'TEXTAREA'
  | 'NUMBER'
  | 'SELECT'
  | 'MULTISELECT'
  | 'RADIO'
  | 'CHECKBOX'
  | 'FILE'
  | 'HEADER'
  | 'HELP_TEXT'
  | 'CITY_PICKER'
  | 'SERVICE_AREA_PICKER';

type DynamicFormValidationRules = {
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  min?: number;
  max?: number;
  minItems?: number;
  maxItems?: number;
  maxFiles?: number;
  maxFileSizeMb?: number;
  allowedMimeTypes?: string[];
  nationalId?: boolean;
  /** 11-digit شناسه ملی اشخاص حقوقی (not the 10-digit personal کد ملی). */
  companyNationalId?: boolean;
  shebaIban?: boolean;
};

type DynamicFormParentField = {
  fieldKey: string;
  mapping: Record<string, string[]>;
};

type DynamicFormField = {
  id: string;
  type: DynamicFormFieldType;
  label: string;
  apiKey: string;
  placeholder?: string | null;
  helpText?: string | null;
  required: boolean;
  options?: string[] | null;
  validationRules?: DynamicFormValidationRules | null;
  parentField?: DynamicFormParentField | null;
  orderIndex: number;
  isMultiple?: boolean;
};

type DynamicFormSchema = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  fields: DynamicFormField[];
  createdAt?: string;
  updatedAt?: string;
};

type DynamicFormSubmitPayload = {
  values: Record<string, unknown>;
  files: Record<string, File[]>;
};

type OptionValue = string;

/**
 * Context handed to a custom field renderer. Lets the host app plug in a
 * bespoke widget (e.g. a province/city picker) for a given field type while
 * DynamicForm keeps owning the value/error/validation lifecycle.
 */
export interface DynamicFormCustomFieldContext {
  field: DynamicFormField;
  value: unknown;
  setValue: (value: unknown) => void;
  error?: string;
  values: Record<string, unknown>;
  /** When true the host widget should render disabled / display-only. */
  readOnly: boolean;
}

export type DynamicFormCustomRenderers = Partial<
  Record<DynamicFormFieldType, (ctx: DynamicFormCustomFieldContext) => React.ReactNode>
>;

export interface DynamicFormProps {
  schema: DynamicFormSchema;
  onSubmit: (payload: DynamicFormSubmitPayload) => Promise<void> | void;
  initialValues?: Record<string, unknown>;
  submitLabel?: string;
  loading?: boolean;
  direction?: 'rtl' | 'ltr';
  className?: string;
  /**
   * Per-field-type render overrides. When a field's `type` has an entry here,
   * this renderer is used instead of the built-in one (the built-in label,
   * help text and error chrome are suppressed so the widget can own them).
   */
  customRenderers?: DynamicFormCustomRenderers;
  /**
   * Display-only mode: all inputs disabled, the submit button is hidden and
   * onSubmit is never invoked. Used to show previously-entered data (e.g.
   * an installer profile under review / approved) without allowing edits.
   */
  readOnly?: boolean;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
  schema,
  onSubmit,
  initialValues,
  submitLabel = 'ثبت فرم',
  loading = false,
  direction,
  className,
  customRenderers,
  readOnly = false,
}) => {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [files, setFiles] = useState<Record<string, File[]>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const resolvedDirection = useMemo(() => {
    if (direction) {
      return direction;
    }
    if (typeof document !== 'undefined' && document.dir) {
      return document.dir === 'rtl' ? 'rtl' : 'ltr';
    }
    return 'rtl';
  }, [direction]);

  const orderedFields = useMemo(
    () => [...(schema.fields || [])].sort((a, b) => a.orderIndex - b.orderIndex),
    [schema.fields],
  );

  useEffect(() => {
    setValues(initialValues || {});
    setFiles({});
    setErrors({});
  }, [initialValues, schema.id, schema.slug]);

  const normalizeExistingFiles = (value: unknown): Array<{ url: string; name: string }> => {
    if (!value) return [];
    if (typeof value === 'string' && value.trim()) {
      return [{ url: value, name: value.split('/').pop() || 'فایل' }];
    }
    if (Array.isArray(value)) {
      return value
        .filter((item) => item && typeof item === 'object' && (item as Record<string, unknown>).url)
        .map((item) => {
          const obj = item as Record<string, unknown>;
          return { url: String(obj.url), name: String(obj.originalName || String(obj.url).split('/').pop() || 'فایل') };
        });
    }
    if (typeof value === 'object' && value !== null) {
      const obj = value as Record<string, unknown>;
      if (obj.url) {
        return [{ url: String(obj.url), name: String(obj.originalName || String(obj.url).split('/').pop() || 'فایل') }];
      }
    }
    return [];
  };

  const validateField = (field: DynamicFormField, value: unknown, fieldFiles?: File[]) => {
    const rules = field.validationRules || {};
    const isMissingValue =
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0);

    if (field.type === 'CITY_PICKER') {
      const sel = (value || {}) as { provinceId?: string; cityId?: string };
      if (field.required && (!sel.provinceId || !sel.cityId)) {
        return 'استان و شهر را انتخاب کنید';
      }
      return null;
    }

    if (field.type === 'SERVICE_AREA_PICKER') {
      const sa = (value || {}) as { provinces?: unknown };
      if (field.required && (!Array.isArray(sa.provinces) || sa.provinces.length === 0)) {
        return 'حداقل یک استان باید انتخاب شود';
      }
      return null;
    }

    if (field.required) {
      if (field.type === 'FILE') {
        const hasNewFiles = fieldFiles && fieldFiles.length > 0;
        const hasExistingFiles = normalizeExistingFiles(value).length > 0;
        if (!hasNewFiles && !hasExistingFiles) {
          return 'این فیلد الزامی است';
        }
      } else if (field.type === 'CHECKBOX' && (!field.options || field.options.length === 0)) {
        if (!value) return 'این فیلد الزامی است';
      } else if (isMissingValue) {
        return 'این فیلد الزامی است';
      }
    }

    if (field.type === 'FILE' && fieldFiles && fieldFiles.length > 0) {
      if (rules.maxFiles !== undefined && fieldFiles.length > Number(rules.maxFiles)) {
        return `حداکثر ${rules.maxFiles} فایل مجاز است`;
      }
      if (rules.maxFileSizeMb !== undefined) {
        const maxBytes = Number(rules.maxFileSizeMb) * 1024 * 1024;
        const oversized = fieldFiles.find((file) => file.size > maxBytes);
        if (oversized) {
          const sizeMb = (oversized.size / (1024 * 1024)).toFixed(2);
          return `حجم فایل «${oversized.name}» (${sizeMb} مگابایت) بیشتر از حد مجاز (${rules.maxFileSizeMb} مگابایت) است`;
        }
      }
      if (Array.isArray(rules.allowedMimeTypes) && rules.allowedMimeTypes.length > 0) {
        const disallowed = fieldFiles.find((file) => !rules.allowedMimeTypes!.includes(file.type));
        if (disallowed) {
          return `نوع فایل «${disallowed.name}» مجاز نیست`;
        }
      }
    }

    if (isMissingValue) return null;

    if ((field.type === 'TEXT' || field.type === 'TEXTAREA') && typeof value === 'string') {
      if (rules.minLength !== undefined && value.length < Number(rules.minLength)) {
        return `حداقل ${rules.minLength} کاراکتر`;
      }
      if (rules.maxLength !== undefined && value.length > Number(rules.maxLength)) {
        return `حداکثر ${rules.maxLength} کاراکتر`;
      }
      if (rules.pattern && !new RegExp(String(rules.pattern)).test(toEnglishDigits(value))) {
        return 'فرمت ورودی معتبر نیست';
      }
    }

    if (field.type === 'NUMBER' && value !== '') {
      const numeric = Number(toEnglishDigits(String(value)));
      if (Number.isNaN(numeric)) {
        return 'عدد معتبر وارد کنید';
      }
      if (rules.min !== undefined && numeric < Number(rules.min)) {
        return `حداقل مقدار ${rules.min}`;
      }
      if (rules.max !== undefined && numeric > Number(rules.max)) {
        return `حداکثر مقدار ${rules.max}`;
      }
    }

    return null;
  };

  /**
   * Resolve the active option list for a (potentially cascading) field.
   * For non-cascading fields this just returns `field.options`. For
   * cascading fields, the list is narrowed to the entry matching the
   * current parent value; if no parent value is set, an empty list is
   * returned so the user is forced to pick the parent first.
   */
  const resolveOptions = (
    field: DynamicFormField,
    currentValues: Record<string, unknown>,
  ): string[] => {
    const baseOptions = field.options || [];
    if (!field.parentField || !field.parentField.fieldKey) {
      return baseOptions;
    }
    const parentValue = currentValues[field.parentField.fieldKey];
    if (typeof parentValue !== 'string' || !parentValue) {
      return [];
    }
    const filtered = field.parentField.mapping?.[parentValue];
    return Array.isArray(filtered) ? filtered : [];
  };

  /**
   * When a parent field changes, any child cascading field that referenced
   * it must drop its stale value (and any cached error) so the user is
   * forced to re-select from the new (filtered) option set.
   */
  const clearStaleCascadeChildren = (
    parentApiKey: string,
    nextValues: Record<string, unknown>,
  ): Record<string, unknown> => {
    let updated = nextValues;
    for (const field of orderedFields) {
      if (field.parentField?.fieldKey === parentApiKey) {
        if (updated[field.apiKey] !== undefined) {
          updated = { ...updated, [field.apiKey]: '' };
        }
      }
    }
    return updated;
  };

  const setFieldValue = (field: DynamicFormField, value: unknown) => {
    setValues((prev) => {
      const next = { ...prev, [field.apiKey]: value };
      return clearStaleCascadeChildren(field.apiKey, next);
    });
    const error = validateField(field, value, files[field.apiKey]);
    setErrors((prev) => {
      // Also clear errors of any cascade children since their value reset.
      const next = { ...prev, [field.apiKey]: error || '' };
      for (const f of orderedFields) {
        if (f.parentField?.fieldKey === field.apiKey) {
          next[f.apiKey] = '';
        }
      }
      return next;
    });
  };

  const setFieldFiles = (field: DynamicFormField, nextFiles: File[]) => {
    setFiles((prev) => ({ ...prev, [field.apiKey]: nextFiles }));
    const error = validateField(field, values[field.apiKey], nextFiles);
    setErrors((prev) => ({ ...prev, [field.apiKey]: error || '' }));
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    for (const field of orderedFields) {
      if (field.type === 'HEADER' || field.type === 'HELP_TEXT') {
        continue;
      }
      const error = validateField(field, values[field.apiKey], files[field.apiKey] || []);
      if (error) {
        nextErrors[field.apiKey] = error;
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (readOnly) return;
    setFormError(null);
    if (!validate()) {
      return;
    }
    try {
      await onSubmit({ values, files });
    } catch (error: any) {
      setFormError(error?.message || 'ارسال فرم ناموفق بود');
    }
  };

  const renderField = (field: DynamicFormField) => {
    const commonInputClass =
      'w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-70';
    const hasError = Boolean(errors[field.apiKey]);

    const customRenderer = customRenderers?.[field.type];
    if (customRenderer) {
      return customRenderer({
        field,
        value: values[field.apiKey],
        setValue: (value) => setFieldValue(field, value),
        error: errors[field.apiKey] || undefined,
        values,
        readOnly,
      });
    }

    switch (field.type) {
      case 'HEADER':
        return <h3 className="text-xl font-bold text-gray-900">{field.label}</h3>;
      case 'HELP_TEXT':
        return <p className="text-sm text-gray-600">{field.helpText || field.label}</p>;
      case 'TEXT':
        return (
          <input
            type="text"
            placeholder={field.placeholder || ''}
            className={`${commonInputClass} ${hasError ? 'border-red-500 bg-red-50' : ''}`}
            value={(values[field.apiKey] as string) || ''}
            onChange={(e) => setFieldValue(field, e.target.value)}
            disabled={readOnly}
          />
        );
      case 'TEXTAREA':
        return (
          <textarea
            rows={5}
            placeholder={field.placeholder || ''}
            className={`${commonInputClass} ${hasError ? 'border-red-500 bg-red-50' : ''}`}
            value={(values[field.apiKey] as string) || ''}
            onChange={(e) => setFieldValue(field, e.target.value)}
            disabled={readOnly}
          />
        );
      case 'NUMBER':
        return (
          <input
            type="text"
            inputMode="numeric"
            placeholder={field.placeholder || ''}
            className={`${commonInputClass} ${hasError ? 'border-red-500 bg-red-50' : ''}`}
            value={(values[field.apiKey] as number | string) ?? ''}
            onChange={(e) => setFieldValue(field, toEnglishDigits(e.target.value))}
            disabled={readOnly}
          />
        );
      case 'SELECT': {
        const activeOptions = resolveOptions(field, values);
        const isAwaitingParent = Boolean(
          field.parentField &&
            (!values[field.parentField.fieldKey] || activeOptions.length === 0),
        );
        return (
          <select
            className={`${commonInputClass} ${hasError ? 'border-red-500 bg-red-50' : ''}`}
            value={(values[field.apiKey] as string) || ''}
            onChange={(e) => setFieldValue(field, e.target.value)}
            disabled={isAwaitingParent || readOnly}
          >
            <option value="">
              {isAwaitingParent
                ? 'ابتدا فیلد قبلی را انتخاب کنید'
                : 'انتخاب کنید'}
            </option>
            {activeOptions.map((option: OptionValue) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      }
      case 'CHECKBOX': {
        const activeOptions = resolveOptions(field, values);
        // Single boolean checkbox — no options defined
        if (activeOptions.length === 0 && !field.parentField) {
          const checked = Boolean(values[field.apiKey]);
          return (
            <input
              type="checkbox"
              checked={checked}
              disabled={readOnly}
              onChange={(e) => setFieldValue(field, e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary"
            />
          );
        }
        // Checkbox group — treat like MULTISELECT
        const selectedValues = Array.isArray(values[field.apiKey])
          ? (values[field.apiKey] as string[])
          : [];
        return (
          <div className="space-y-2">
            {activeOptions.length === 0 && field.parentField && (
              <p className="text-xs text-gray-500">ابتدا فیلد قبلی را انتخاب کنید</p>
            )}
            {activeOptions.map((option: OptionValue) => {
              const checked = selectedValues.includes(option);
              return (
                <label key={option} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={readOnly}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...selectedValues, option]
                        : selectedValues.filter((value) => value !== option);
                      setFieldValue(field, next);
                    }}
                  />
                  {option}
                </label>
              );
            })}
          </div>
        );
      }
      case 'MULTISELECT': {
        const selectedValues = Array.isArray(values[field.apiKey])
          ? (values[field.apiKey] as string[])
          : [];
        const activeOptions = resolveOptions(field, values);
        return (
          <div className="space-y-2">
            {activeOptions.length === 0 && field.parentField && (
              <p className="text-xs text-gray-500">ابتدا فیلد قبلی را انتخاب کنید</p>
            )}
            {activeOptions.map((option: OptionValue) => {
              const checked = selectedValues.includes(option);
              return (
                <label key={option} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={readOnly}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...selectedValues, option]
                        : selectedValues.filter((value) => value !== option);
                      setFieldValue(field, next);
                    }}
                  />
                  {option}
                </label>
              );
            })}
          </div>
        );
      }
      case 'RADIO': {
        const activeOptions = resolveOptions(field, values);
        return (
          <div className="space-y-2">
            {activeOptions.length === 0 && field.parentField && (
              <p className="text-xs text-gray-500">ابتدا فیلد قبلی را انتخاب کنید</p>
            )}
            {activeOptions.map((option: OptionValue) => (
              <label key={option} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name={field.apiKey}
                  checked={values[field.apiKey] === option}
                  disabled={readOnly}
                  onChange={() => setFieldValue(field, option)}
                />
                {option}
              </label>
            ))}
          </div>
        );
      }
      case 'FILE': {
        const fieldFiles = files[field.apiKey] || [];
        const existingFiles = normalizeExistingFiles(values[field.apiKey]);
        return (
          <div className="space-y-2">
            {existingFiles.length > 0 && (
              <ul className="space-y-1">
                {existingFiles.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="text-blue-600 underline break-all hover:text-blue-800"
                    >
                      {f.name}
                    </a>
                  </li>
                ))}
              </ul>
            )}
            {!readOnly && (
              <>
                <input
                  type="file"
                  multiple={Boolean(field.isMultiple)}
                  onChange={(e) => {
                    const pickedFiles = Array.from(e.target.files || []);
                    setFieldFiles(field, pickedFiles);
                  }}
                />
                {fieldFiles.length > 0 && (
                  <ul className="space-y-1 text-sm text-gray-600">
                    {fieldFiles.map((file) => (
                      <li key={`${file.name}-${file.size}`}>{file.name}</li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <form
      className={`space-y-5 ${className || ''}`}
      dir={resolvedDirection}
      onSubmit={handleSubmit}
      noValidate
    >
      {formError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {formError}
        </div>
      )}

      {orderedFields.map((field) => {
        // Custom-rendered fields own their own label/error chrome.
        const isCustom = Boolean(customRenderers?.[field.type]);
        return (
          <div key={field.id || field.apiKey} className="space-y-2">
            {field.type !== 'HEADER' &&
              field.type !== 'HELP_TEXT' &&
              !isCustom && (
                <label className="block text-sm font-medium text-gray-700">
                  {field.label}
                  {field.required && <span className="mr-1 text-red-500">*</span>}
                </label>
              )}
            {renderField(field)}
            {field.helpText && field.type !== 'HELP_TEXT' && (
              <p className="text-xs text-gray-500">{field.helpText}</p>
            )}
            {!isCustom && errors[field.apiKey] && (
              <p className="text-xs text-red-600">{errors[field.apiKey]}</p>
            )}
          </div>
        );
      })}

      {!readOnly && (
        <button
          type="submit"
          disabled={loading}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-primary px-5 text-body-md font-medium text-primary-foreground shadow-level-1 transition-all hover:bg-primary-hover hover:shadow-level-2 focus-visible:outline-none focus-visible:shadow-focus-ring disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none"
        >
          {loading ? 'در حال ارسال...' : submitLabel}
        </button>
      )}
    </form>
  );
};
